import { ref, onUnmounted } from 'vue'
import Pusher from 'pusher-js'
import api from '@/config/api'
import store from '@/store'
import { setSocketId } from '@/services/socketId'

/**
 * Pusher-based WebSocket service for Laravel Reverb
 * Provides reliable real-time communication with presence channel support
 */
class PusherService {
  constructor() {
    this.pusher = null
    this.channels = new Map()
    this.isConnected = ref(false)
    this.connectionError = ref(null)
    this.listeners = new Map()
  }

  /**
   * Initialize Pusher connection
   */
  connect() {
    if (this.pusher) {
      console.log('[Pusher] Already connected')
      return
    }

    const appKey = import.meta.env.VITE_REVERB_APP_KEY || 'newsdesk-key'
    const host = import.meta.env.VITE_REVERB_HOST || 'newsdesk-api.scrippsnet.io'
    const port = import.meta.env.VITE_REVERB_PORT || 443
    const scheme = import.meta.env.VITE_REVERB_SCHEME || 'wss'
    const basePath = import.meta.env.VITE_REVERB_PATH || '/wss'
    const forceTLS = scheme === 'wss'

    // Construct the WebSocket path for Caddy/Reverb
    // Pusher.js will append /app/{key} automatically, so we just provide the base path
    const wsPath = basePath

    console.log('[Pusher] Connecting...', { host, port, forceTLS, wsPath })

    this.pusher = new Pusher(appKey, {
      wsHost: host,
      wsPort: port,
      wssPort: port,
      wsPath,
      forceTLS,
      enabledTransports: ['ws', 'wss'],
      disableStats: true,
      cluster: 'mt1', // Required by Pusher but not used by Reverb
      // Use custom authorizer with axios (benefits from token refresh interceptor)
      authorizer: (channel) => {
        return {
          authorize: (socketId, callback) => {
            api
              .post('/broadcasting/auth', {
                socket_id: socketId,
                channel_name: channel.name
              })
              .then((response) => callback(null, response.data))
              .catch((error) => {
                console.error('[Pusher] Authorization failed:', error)
                callback(new Error('Auth failed'), null)
              })
          }
        }
      }
    })

    // Connection state handlers
    this.pusher.connection.bind('connected', () => {
      console.log('[Pusher] Connected successfully')
      this.isConnected.value = true
      this.connectionError.value = null
      // Published so axios can send it as X-Socket-ID: the API excludes this
      // connection from the broadcast of any change it makes (ND-2335).
      setSocketId(this.pusher.connection.socket_id)
    })

    this.pusher.connection.bind('disconnected', () => {
      console.log('[Pusher] Disconnected')
      this.isConnected.value = false
      // A stale id would exclude a connection that no longer exists, silently
      // dropping the echo suppression for whoever reconnects into it.
      setSocketId(null)
    })

    this.pusher.connection.bind('error', (error) => {
      this.connectionError.value = error.message || 'Connection error'
    })

    this.pusher.connection.bind('state_change', (states) => {
      console.log('[Pusher] State change:', states.previous, '->', states.current)
    })

    // Subscribe to server-info for demo
    this.subscribe('server-info')

    // Subscribe to station-specific channels if we have a station
    const currentStation = store.getters.currentStation
    if (currentStation?.station) {
      this.subscribeToStation(currentStation.station)
    }
  }

  /**
   * Disconnect from Pusher
   */
  disconnect() {
    if (this.pusher) {
      console.log('[Pusher] Disconnecting...')
      this.pusher.disconnect()
      this.pusher = null
      setSocketId(null)
      this.channels.clear()
      this.listeners.clear()
      this.isConnected.value = false
    }
  }

  /**
   * Subscribe to station-specific channels
   */
  subscribeToStation(station) {
    this.subscribe(`station.${station}`)
    this.subscribe(`tasks.${station}`)
    this.subscribe(`media.${station}`)
  }

  /**
   * Subscribe to a public channel
   * @param {string} channelName - Name of the channel
   * @returns {object} - Pusher channel object
   */
  subscribe(channelName) {
    if (!this.pusher) {
      console.warn('[Pusher] Cannot subscribe - not connected')
      return null
    }

    if (this.channels.has(channelName)) {
      return this.channels.get(channelName)
    }

    console.log('[Pusher] Subscribing to channel:', channelName)
    const channel = this.pusher.subscribe(channelName)

    channel.bind('pusher:subscription_succeeded', () => {
      console.log('[Pusher] Successfully subscribed to:', channelName)
    })

    channel.bind('pusher:subscription_error', (error) => {
      console.error('[Pusher] Subscription error:', channelName, error)
    })

    this.channels.set(channelName, channel)
    return channel
  }

  /**
   * Subscribe to a presence channel (for collaborative features)
   * @param {string} channelName - Name of the presence channel (without 'presence-' prefix)
   * @param {object} callbacks - Object with onJoin, onLeave, onMembers callbacks
   * @returns {object} - Pusher presence channel object
   */
  subscribePresence(channelName, callbacks = {}) {
    if (!this.pusher) {
      console.warn('[Pusher] Cannot subscribe to presence channel - not connected')
      return null
    }

    const fullChannelName = channelName.startsWith('presence-') ? channelName : `presence-${channelName}`

    // If channel already exists, unsubscribe first to ensure clean state
    if (this.channels.has(fullChannelName)) {
      console.log('[Pusher] Re-subscribing to presence channel (cleanup existing):', fullChannelName)
      this.unsubscribe(fullChannelName)
    }

    console.log('[Pusher] Subscribing to presence channel:', fullChannelName)
    const channel = this.pusher.subscribe(fullChannelName)

    // Handle subscription success
    channel.bind('pusher:subscription_succeeded', (members) => {
      console.log('[Pusher] Presence channel subscribed:', fullChannelName, members.count, 'members')
      if (callbacks.onMembers) {
        callbacks.onMembers(members)
      }
    })

    // Handle subscription errors
    channel.bind('pusher:subscription_error', (error) => {
      console.error('[Pusher] Presence subscription error:', fullChannelName, error)
    })

    // Handle member added
    channel.bind('pusher:member_added', (member) => {
      console.log('[Pusher] Member joined:', member.id)
      if (callbacks.onJoin) {
        callbacks.onJoin(member)
      }
    })

    // Handle member removed
    channel.bind('pusher:member_removed', (member) => {
      console.log('[Pusher] Member left:', member.id)
      if (callbacks.onLeave) {
        callbacks.onLeave(member)
      }
    })

    this.channels.set(fullChannelName, channel)
    return channel
  }

  /**
   * Unsubscribe from a channel
   * @param {string} channelName - Name of the channel
   */
  unsubscribe(channelName) {
    if (!this.pusher) {
      return
    }

    // Add presence- prefix if not already present and channel might be a presence channel
    const possibleNames = [channelName]
    if (!channelName.startsWith('presence-')) {
      possibleNames.push(`presence-${channelName}`)
    }

    for (const name of possibleNames) {
      if (this.channels.has(name)) {
        console.log('[Pusher] Unsubscribing from channel:', name)
        const channel = this.channels.get(name)

        // Unbind all events from the channel
        if (channel && channel.unbind) {
          channel.unbind_all()
        }

        this.pusher.unsubscribe(name)
        this.channels.delete(name)

        // Clean up listeners for this channel
        const keysToDelete = []
        for (const [key] of this.listeners) {
          if (key.startsWith(`${name}:`)) {
            keysToDelete.push(key)
          }
        }
        keysToDelete.forEach((key) => this.listeners.delete(key))
      }
    }
  }

  /**
   * Listen for events on a channel
   * @param {string} channelName - Name of the channel
   * @param {string} eventName - Name of the event
   * @param {function} callback - Callback function
   * @returns {function} - Cleanup function to remove the listener
   */
  on(channelName, eventName, callback) {
    const channel = this.channels.get(channelName) || this.subscribe(channelName)
    if (!channel) {
      console.warn('[Pusher] Cannot bind event - channel not available:', channelName)
      return () => {}
    }

    // Add to internal listeners map
    const key = `${channelName}:${eventName}`
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set())
    }
    this.listeners.get(key).add(callback)

    // Bind to Pusher channel
    channel.bind(eventName, callback)

    console.log('[Pusher] Listening to event:', channelName, eventName)

    // Return cleanup function
    return () => {
      channel.unbind(eventName, callback)
      const listeners = this.listeners.get(key)
      if (listeners) {
        listeners.delete(callback)
        if (listeners.size === 0) {
          this.listeners.delete(key)
        }
      }
    }
  }

  /**
   * Trigger a client event on a presence/private channel
   * Client events must start with "client-"
   * @param {string} channelName - Name of the channel
   * @param {string} eventName - Name of the event (will be prefixed with "client-" if not already)
   * @param {*} data - Data to send
   */
  trigger(channelName, eventName, data) {
    const channel = this.channels.get(channelName)
    if (!channel) {
      console.warn('[Pusher] Cannot trigger event - channel not subscribed:', channelName)
      return
    }

    // Ensure event name starts with "client-"
    const clientEventName = eventName.startsWith('client-') ? eventName : `client-${eventName}`

    channel.trigger(clientEventName, data)
  }

  /**
   * Get the current user's presence info for a channel
   * @param {string} channelName - Name of the presence channel
   * @returns {object|null} - Current user's member info or null
   */
  getMe(channelName) {
    const fullChannelName = channelName.startsWith('presence-') ? channelName : `presence-${channelName}`

    const channel = this.channels.get(fullChannelName)
    if (!channel || !channel.members) {
      return null
    }

    return channel.members.me
  }

  /**
   * Get all members in a presence channel
   * @param {string} channelName - Name of the presence channel
   * @returns {object} - Members object with each() and count methods
   */
  getMembers(channelName) {
    const fullChannelName = channelName.startsWith('presence-') ? channelName : `presence-${channelName}`

    const channel = this.channels.get(fullChannelName)
    if (!channel || !channel.members) {
      return { count: 0, each: () => {} }
    }

    return channel.members
  }
}

// Create singleton instance
export const pusherService = new PusherService()

/**
 * Vue composable for Pusher functionality
 */
export function usePusher() {
  const cleanup = []

  /**
   * Subscribe to channel events
   */
  const subscribe = (channel, event, callback) => {
    const unsubscribe = pusherService.on(channel, event, callback)
    cleanup.push(unsubscribe)
    return unsubscribe
  }

  /**
   * Unsubscribe from channel
   */
  const unsubscribe = (channelName) => {
    pusherService.unsubscribe(channelName)
  }

  /**
   * Subscribe to presence channel
   */
  const subscribePresence = (channelName, callbacks) => {
    const channel = pusherService.subscribePresence(channelName, callbacks)
    cleanup.push(() => pusherService.unsubscribe(channelName))
    return channel
  }

  // Clean up on component unmount
  onUnmounted(() => {
    cleanup.forEach((fn) => fn())
  })

  return {
    isConnected: pusherService.isConnected,
    connectionError: pusherService.connectionError,
    subscribe,
    subscribePresence,
    unsubscribe,
    trigger: (channel, event, data) => pusherService.trigger(channel, event, data),
    getMe: (channel) => pusherService.getMe(channel),
    getMembers: (channel) => pusherService.getMembers(channel),
    connect: () => pusherService.connect(),
    disconnect: () => pusherService.disconnect()
  }
}
